import { useCallback, useMemo } from "react";
import { generatePath, Link, useNavigate } from "react-router";

import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import { cn } from "@/lib/chillnn/cn";
import { getRoomDetailUrl } from "@/lib/chillnn/get-detail-url";
import { isDummyRoom } from "@/lib/chillnn/is-dummy-room";
import { SaleTypeForPlan, type Room, type RoomDummy } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";

import DragIndicator from "@/components/ui/icon/drag-indicator";
import { Image } from "@/components/ui/image/image";
import { Label } from "@/components/ui/label/label";
import { TableCell } from "@/components/ui/table/table";

type Props = {
	hotelID: string;
	room: RoomDummy | Room;

	onDeleteRoom: (roomID: string) => void;

	isSorting?: boolean;
};

export const RoomListPageTableItem = ({ room, hotelID, onDeleteRoom: _onDeleteRoom, isSorting }: Props) => {
	const imageURL = getImageUrl(room.mainKeyVisual);

	const saleTypeForPlanText = (room as Room)?.saleTypeForPlan === SaleTypeForPlan.Limited ? "プラン限定" : "通常";
	const inSaleTypeText = room.inSale ? { variant: "green", text: "公開" } : { variant: "pink", text: "非公開" };
	const navigate = useNavigate();
	const roomID = room.roomID;

	const editRoomPagePath = useMemo(() => {
		if (isDummyRoom(room)) {
			return generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.ROOM_ID.DUMMY, { hotelID, roomID });
		} else {
			return generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.ROOM_ID.DEFAULT, { hotelID, roomID });
		}
	}, [hotelID, roomID, room]);
	const onTransitionToEdit = useCallback(() => {
		navigate(editRoomPagePath);
	}, [editRoomPagePath, navigate]);
	const onCopyRoom = useCallback(() => {
		let createNewRoomPagePath = "";

		if (isDummyRoom(room)) {
			createNewRoomPagePath = generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.NEW.DUMMY.DEFAULT, { hotelID });
		} else {
			createNewRoomPagePath = generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.NEW.ROOM.DEFAULT, { hotelID });
		}
		/**
		 * @todo: クエリパラメータ（duplicateItemID）の管理方法を定義したい
		 */
		navigate(createNewRoomPagePath + `?duplicateItemID=${roomID}`);
	}, [hotelID, navigate, room, roomID]);
	const onTransitionToDetail = useCallback(() => {
		const roomDetailURL = getRoomDetailUrl(room.hotelID, roomID);
		window.open(roomDetailURL, "_blank");
	}, [room.hotelID, roomID]);
	const onDeleteRoom = useCallback(() => {
		_onDeleteRoom(roomID);
	}, [_onDeleteRoom, roomID]);

	return (
		<>
			<TableCell className={cn(!isSorting && "hidden")}>
				<div className="grid place-content-center">
					<div className="shrink-0 rounded p-1 hover:cursor-grab hover:bg-zinc-100">
						<DragIndicator size="default" />
					</div>
				</div>
			</TableCell>
			{/* 部屋名 */}
			<TableCell className="flex items-center gap-x-3">
				<Image src={imageURL} className="h-16 w-16 min-w-16 shrink-0 rounded object-cover" />

				<Link to={editRoomPagePath} className="underline-offset-4 hover:underline" viewTransition>
					{room.name}
				</Link>
			</TableCell>
			{/* ステータス（公開/非公開） */}
			<TableCell className="text-center">
				<Label variant={inSaleTypeText.variant as never} text={inSaleTypeText.text} />
			</TableCell>
			{/* 定員 */}
			<TableCell className="text-right">
				<p>
					定員{"　"}
					{`${room.capacityPeopleMaximum}人`}
				</p>
				<p>
					大人{"　"}
					{`${room.capacityAdultMaximum}人`}
				</p>
				{!room.isKidsUnavailable && (
					<p>
						子ども{"　"}
						{`${room.capacityKidsMaximum}人`}
					</p>
				)}
			</TableCell>
			{/* 総在庫 */}
			<TableCell className="text-center">
				<p>{room.maxRoomNum}</p>
			</TableCell>
			{/* 販売タイプ */}
			<TableCell className="text-center">
				<p>{saleTypeForPlanText}</p>
			</TableCell>
			{/* 操作 */}
			<TableCell>
				<div className="flex justify-center">
					<IconButtonList
						onClickCopyButton={onCopyRoom}
						onClickDeleteButton={onDeleteRoom}
						onClickEditButton={onTransitionToEdit}
						onClickOpenButton={onTransitionToDetail}
					/>
				</div>
			</TableCell>
		</>
	);
};
